/** Console v0.5 Team and invites: the words and rules the screens show
 * (design brief v0.5 s.5-6; session A's invite contract, #367). Pure -- no
 * DOM -- so it's tested with Node (web/console/test/v05-team.test.ts). The
 * server is the authority on every rule; this only decides what's shown. */

export type Capability = "prepare" | "review" | "approve" | "lodge" | "account_admin";

/** The order the invite dialog and the People table show them in (brief s.5). */
export const CAPABILITIES: ReadonlyArray<{ id: Capability; label: string }> = [
  { id: "prepare", label: "Prepare" },
  { id: "review", label: "Review" },
  { id: "approve", label: "Approve" },
  { id: "lodge", label: "Lodge" },
  { id: "account_admin", label: "Account admin" },
];

/** What the invite dialog starts with ticked (Team mock-up): Prepare and Review. */
export const DEFAULT_CAPABILITIES: readonly Capability[] = ["prepare", "review"];

export type InviteStatus = "pending" | "expired" | "accepting" | "used" | "revoked" | "failed";

export interface Invite {
  invite_id: string;
  email: string;
  capabilities: Capability[];
  organisation_ids: string[] | null; // null = all organisations, including new ones
  status: InviteStatus;
  created_at: string;
  expires_at: string;
}

export type Delivery = "sent" | "skipped_test_domain" | "not_configured" | "failed";

export const COPY = {
  invite: "Invite someone",
  workEmail: "Work email",
  whatCanTheyDo: "What can they do?",
  whichOrganisations: "Which organisations?",
  allOrganisations: "All organisations, including new ones",
  chosenOrganisations: "Only the ones I choose",
  inviteFootnote: "They'll get an email link valid for 7 days. Before they see anything, they set a password and an authenticator app.",
  sent: "Invite sent. It's valid for 7 days. They'll set a password and an authenticator app before they can see anything.",
  notSent: "The invite was made, but the email couldn't be sent. Use Resend on Pending invites to try again.",
  testDomain: "Test address: no email was sent. Use the accept link below.",
  invitesExpire: "Invites expire after 7 days.",
  noInvites: "No invites waiting.",
  noInvitesBody: "Invite someone, and they'll show here until they accept.",
  resend: "Resend",
  cancelInvite: "Cancel invite",
  cancelQuestion: "Cancel this invite?",
  cancelBody: "The link in their email stops working. You can invite them again later.",
  keepInvite: "Keep invite",
  pickOne: "Choose at least one thing they can do.",
  pickOrganisation: "Choose at least one organisation, or give them all organisations.",
  badEmail: "Enter a work email address, like name@example.com.",
} as const;

export function capabilityLabels(caps: readonly string[]): string[] {
  return CAPABILITIES.filter((c) => caps.includes(c.id)).map((c) => c.label);
}

/** "Prepare, Review · 4 organisations" / "… · All organisations" / "… · Their company". */
export function inviteDetail(invite: Pick<Invite, "capabilities" | "organisation_ids">, accountType: "practice" | "company"): string {
  const caps = capabilityLabels(invite.capabilities).join(", ") || "No capabilities";
  if (accountType === "company") return caps;
  const orgs = invite.organisation_ids;
  const where = orgs === null ? "All organisations" : `${orgs.length} ${orgs.length === 1 ? "organisation" : "organisations"}`;
  return `${caps} · ${where}`;
}

/** "12 Sep", in the viewer's own time zone (PRODUCT-UI.md s.5: dates in lists). */
export function shortDate(iso: string, timeZone: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // Three-letter months always ("12 Sep", PRODUCT-UI.md s.5): some browsers write "Sept" for en-AU.
  const parts = new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone }).format(d).split("-");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${Number(parts[2])} ${months[Number(parts[1]) - 1]}`;
}

/** The right-hand label on a Pending invites row. */
export function inviteStateLabel(invite: Pick<Invite, "status" | "expires_at">, timeZone: string): string {
  switch (invite.status) {
    case "pending":
      return `Expires ${shortDate(invite.expires_at, timeZone)}`;
    case "accepting":
      return "Accepting now";
    case "expired":
      return "Expired";
    case "used":
      return "Accepted";
    case "revoked":
      return "Cancelled";
    case "failed":
      return "Couldn't be set up";
  }
}

/** Resend works for a pending or expired invite; cancel only for a pending one (A: 409 invite_not_pending otherwise). */
export function canResend(status: InviteStatus): boolean {
  return status === "pending" || status === "expired";
}

export function canRevoke(status: InviteStatus): boolean {
  return status === "pending";
}

/** What to tell the admin about the email, after an invite or a resend. */
export function deliveryNotice(delivery: Delivery): { text: string; ok: boolean } {
  switch (delivery) {
    case "sent":
      return { text: COPY.sent, ok: true };
    case "skipped_test_domain":
      return { text: COPY.testDomain, ok: true };
    default:
      return { text: COPY.notSent, ok: false };
  }
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface InviteForm {
  email: string;
  capabilities: Capability[];
  allOrganisations: boolean;
  organisationIds: string[];
}

/** The form's own checks, in the screen's words; the server checks again (400s). */
export function inviteFormProblem(form: InviteForm, accountType: "practice" | "company"): string | null {
  if (!EMAIL.test(form.email.trim())) return COPY.badEmail;
  if (form.capabilities.length === 0) return COPY.pickOne;
  if (accountType === "practice" && !form.allOrganisations && form.organisationIds.length === 0) return COPY.pickOrganisation;
  return null;
}

/** The POST /api/team/invites body: a company's people get the company (organisation_ids null). */
export function inviteBody(form: InviteForm, accountType: "practice" | "company"): { email: string; capabilities: Capability[]; organisation_ids: string[] | null } {
  const caps = CAPABILITIES.map((c) => c.id).filter((id) => form.capabilities.includes(id));
  return {
    email: form.email.trim(),
    capabilities: caps,
    organisation_ids: accountType === "company" || form.allOrganisations ? null : [...form.organisationIds],
  };
}

// ---- The accept page (/invite/<token>) ----

const TOKEN = /^[A-Za-z0-9_-]{20,120}$/;

/** The invite link is /invite#<token> (D, #368): the token stays in the fragment, which browsers never send, so it is
 * never in a request line, an app log or the WAF's logs. The page reads it once and then removes it from the address
 * bar. Returns the token, "" for the invite page without a usable one, or null for any other page. */
export function inviteToken(pathname: string, hash: string): string | null {
  if (!/^\/invite\/?$/.test(pathname)) return null;
  const t = hash.startsWith("#") ? hash.slice(1) : hash;
  return TOKEN.test(t) ? t : "";
}

/** The password rules, in the server's own words (A: at least 12 characters with a lower-case letter, an upper-case
 * letter, a number and a symbol). Each is shown as a line that ticks off as it's met. */
export const PASSWORD_RULES: ReadonlyArray<{ label: string; ok: (pw: string) => boolean }> = [
  { label: "At least 12 characters", ok: (pw) => pw.length >= 12 },
  { label: "A lower-case letter", ok: (pw) => /[a-z]/.test(pw) },
  { label: "An upper-case letter", ok: (pw) => /[A-Z]/.test(pw) },
  { label: "A number", ok: (pw) => /[0-9]/.test(pw) },
  { label: "A symbol", ok: (pw) => /[^A-Za-z0-9]/.test(pw) },
];

export function passwordMissing(pw: string): string[] {
  return PASSWORD_RULES.filter((r) => !r.ok(pw)).map((r) => r.label);
}

/** The accept page's dead ends (A's 404/410/409/503), in words that say what to do next. */
export function inviteDeadEnd(status: number, code: string | null): { title: string; body: string } {
  if (status === 410 && code === "invite_expired") {
    return { title: "This invite has expired", body: "Invites last 7 days. Ask your account admin to send you a new one." };
  }
  if (status === 410 && code === "invite_used") {
    return { title: "This invite has already been used", body: "If it was you, sign in to Preekie. If not, tell your account admin." };
  }
  if (status === 410 && code === "invite_revoked") {
    return { title: "This invite was cancelled", body: "Ask your account admin if you still need access." };
  }
  if ((status === 410 && code === "invite_failed") || (status === 503 && code === "invite_setup_failed")) {
    return { title: "This invite couldn't be set up", body: "Ask your account admin to send you a new invitation." };
  }
  if (status === 409 && code === "accept_in_progress") {
    return { title: "This invite is being accepted", body: "Wait a minute, then open the link again." };
  }
  if (status === 409 && code === "account_exists") {
    return { title: "You already have a Preekie sign-in", body: "Sign in with that email address. Your account admin can add you from there." };
  }
  return { title: "This link doesn't work", body: "Open the link from your invite email again. If it still doesn't work, a newer invite may have replaced it: ask your account admin to send it again." };
}

// ---- People and approval rules (A's #373) ----

export const PEOPLE_COPY = {
  differentPerson: "A different person must approve",
  differentPersonHint: "Whoever prepared or reviewed a piece of work can't also approve it.",
  selfApproval: "Allow self-approval when only one approver exists",
  selfApprovalHint: "For small teams. Each self-approval is recorded with a warning.",
  lodging: "Lodging",
  lodgingHint: "Only people who can lodge may release approved work.",
  alwaysOn: "Always on",
  removeBody: "They lose access to this account straight away, and any session they have open is ended.",
  adminsOnly: "Only an account admin can see and change the team.",
  twoFactorRequired: "Required",
} as const;

/** "Pat Lee", else the email, else a dash (people who joined before invites existed have neither). */
export function personName(p: { display_name: string | null; email: string | null }): string {
  return p.display_name?.trim() || p.email?.trim() || "–";
}

/** How a sentence or a button names someone: their name or email, else "this person" (never a bare dash). */
export function personRef(p: { display_name: string | null; email: string | null }, start = false): string {
  const n = personName(p);
  return n !== "–" ? n : start ? "This person" : "this person";
}

/** The Organisation access column. */
export function accessLabel(organisationIds: readonly string[] | null, accountType: "practice" | "company", accountName: string | null): string {
  if (accountType === "company") return accountName ?? "This company";
  if (organisationIds === null) return "All organisations";
  const n = organisationIds.length;
  return `${n} ${n === 1 ? "organisation" : "organisations"}`;
}

/** The note under People, from the account's real limits (no claim the plan doesn't make). */
export function seatNote(limits: { users: { used: number; max: number } } | null | undefined): string | null {
  if (!limits) return null;
  const { used, max } = limits.users;
  const left = Math.max(0, max - used);
  return `You're using ${used} of ${max} ${max === 1 ? "user" : "users"} on your plan${left === 0 ? ". Ask Preekie to raise the limit to add more." : "."}`;
}

/** What changed in the Edit dialog, as the POST body (only what changed; null when nothing did). */
export function memberChange(
  before: { capabilities: readonly string[]; organisation_ids: readonly string[] | null },
  after: { capabilities: Capability[]; organisation_ids: string[] | null }
): { capabilities?: Capability[]; organisation_ids?: string[] | null } | null {
  const caps = CAPABILITIES.map((c) => c.id).filter((id) => after.capabilities.includes(id));
  const sameCaps = caps.length === before.capabilities.length && caps.every((c) => before.capabilities.includes(c));
  const a = after.organisation_ids;
  const b = before.organisation_ids;
  const sameOrgs = a === null || b === null ? a === b : a.length === b.length && a.every((x) => b.includes(x));
  const change: { capabilities?: Capability[]; organisation_ids?: string[] | null } = {};
  if (!sameCaps) change.capabilities = caps;
  if (!sameOrgs) change.organisation_ids = a === null ? null : [...a];
  return Object.keys(change).length ? change : null;
}

// ---- Preekie service team (A's #382, contracts s.16) ----

export const SERVICE_COPY = {
  title: "Preekie service team",
  body: "If you use Preekie's accounting or tax services, our service staff need access to do the work. You decide what they can see, and you can withdraw access at any time. Every action they take is logged.",
  canApprove: "Can approve or lodge",
  canApproveAnswer: "No. Only your team can.",
  grant: "Grant access",
  notGranted: "Not granted",
  granted: "Granted",
  none: "None. Only needed if you outsource work to Preekie.",
  daysHint: "Access ends by itself after this many days (1 to 90). You can withdraw it sooner.",
  withdrawBody: "They lose access on their next click, and any session they have open in this account ends.",
} as const;

export const SERVICE_NAMES: ReadonlyArray<{ id: string; label: string }> = [
  { id: "accounting", label: "Accounting" },
  { id: "tax", label: "Tax" },
  { id: "assurance", label: "Assurance" },
  { id: "financing", label: "Financing" },
];

/** "Accounting and tax for 2 organisations", for a grant's scope. */
export function grantScope(services: readonly string[], organisationCount: number, accountType: "practice" | "company"): string {
  const names = SERVICE_NAMES.filter((s) => services.includes(s.id)).map((s) => s.label.toLowerCase());
  const list = names.length <= 1 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  const what = list ? list.charAt(0).toUpperCase() + list.slice(1) : "No services";
  if (accountType === "company") return what;
  return `${what} for ${organisationCount} ${organisationCount === 1 ? "organisation" : "organisations"}`;
}

export function grantProblem(g: { userId: string; organisationIds: readonly string[]; services: readonly string[]; days: number }): string | null {
  if (!g.userId) return "Choose who gets access.";
  if (!g.organisationIds.length) return "Choose at least one organisation.";
  if (!g.services.length) return "Choose at least one service.";
  if (!Number.isInteger(g.days) || g.days < 1 || g.days > 90) return "Choose 1 to 90 days.";
  return null;
}
